import type { FC, ReactElement } from 'react';
import { useState } from 'react';

import { useCashierCallbackConnected } from '../../../hooks/useCashierCallbackConnected';
import { useRedirectToLobbyCallbackConnected } from '../../../hooks/useRedirectToLobbyCallbackConnected';
import { useCoreDispatch } from '../../../store/CoreStore';
import { headerActions } from '../../../store/slices/header/slice';
import { HeaderModal } from '../../../store/slices/header/types';
import { usePopupSlice } from '../../../store/slices/popup/selectors';
import { popupActions } from '../../../store/slices/popup/slice';
import { type MessageAction, MessageActionType, MessageLinkType } from '../../../store/slices/popup/types';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { Loader } from '../../loader/Loader';
import { PopupType } from '../../popup/types';

import styles from './popupContent.module.css';

export const ServerMessageConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const { text, title, options = [] } = usePopupSlice.customPopup()!;
  const cashierCallback = useCashierCallbackConnected();
  const [isLoading, setLoad] = useState(false);
  const redirectToLobby = useRedirectToLobbyCallbackConnected();

  const actionsElements = options.map((action: MessageAction) => {
    const onClick = (): void => {
      switch (action.action) {
        case MessageActionType.Cashier:
          cashierCallback?.();
          dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
          break;
        case MessageActionType.Quit:
          redirectToLobby();
          break;
        case MessageActionType.Continue:
          dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
          break;
        case MessageActionType.History:
          dispatch(headerActions.setOpenedModal(HeaderModal.History));
          dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
          break;
        case MessageActionType.Link:
          switch (action.linkType) {
            case MessageLinkType.Redirect:
              window.location.href = action.url || '';
              break;
            case MessageLinkType.Open:
              window.open(action.url || '', '_blank');
              dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
              break;
            case MessageLinkType.Notify:
              window.top?.postMessage(action.url || '');
              dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
              break;
            case MessageLinkType.Ajax:
              fetch(action.url || '')
                .then()
                .catch((e) => {
                  console.log(e);
                  //FIXME Log error here
                });
              dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
              break;
            case MessageLinkType.AjaxResponse:
              setLoad(true);
              fetch(action.url || '')
                .then((response) => {
                  if (response.ok) {
                    dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
                  } else {
                    dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
                    dispatch(popupActions.overwritePopup(PopupType.GenericError));
                    //FIXME Log error here
                  }
                })
                .catch(() => {
                  dispatch(popupActions.dismissPopup(PopupType.CustomPopup));
                  dispatch(popupActions.overwritePopup(PopupType.GenericError));
                  //FIXME Log error here
                });
              break;
          }
          break;
      }
    };
    return (
      <Button key={action.label} variant={ButtonVariant.ACCENT} onClick={onClick}>
        {action.label}
      </Button>
    );
  });

  return (
    <div className={styles.popup}>
      <div className={styles.popupContent}>
        {title && <div className={styles.popupContent__title}>{title}</div>}
        {text && <div className={styles.popupContent__message}>{text}</div>}
        {options.length > 0 &&
          (isLoading ? <Loader /> : <div className={styles.popupContent__buttons}>{actionsElements}</div>)}
      </div>
    </div>
  );
};
